/**
 * Cognito hosted-UI SSO: PKCE authorize redirect + code→token exchange.
 *
 * Enabled when VITE_COGNITO_DOMAIN + VITE_COGNITO_CLIENT_ID are baked at
 * build time; ssoEnabled() gates every UI entry point so the demo build
 * (no vars) is unchanged. No client secret exists for the SPA client —
 * PKCE (S256) is the CSRF/interception defense, per OAuth for browser apps.
 */

const VERIFIER_KEY = 'sso_pkce_verifier';

function domain(): string {
  return (import.meta.env.VITE_COGNITO_DOMAIN as string | undefined) ?? '';
}

function clientId(): string {
  return (import.meta.env.VITE_COGNITO_CLIENT_ID as string | undefined) ?? '';
}

export function ssoEnabled(): boolean {
  return Boolean(domain() && clientId());
}

function redirectUri(): string {
  return `${window.location.origin}/callback`;
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Send the browser to the hosted UI. Corporate sign-in hands off directly to
 * the Entra IdP, skipping Cognito's account-chooser page. `internal: true`
 * keeps the chooser — its password form is the only working door for pool
 * (test/ops) accounts, since the API's own /auth/login is demo-mode only.
 */
export async function beginSsoLogin(
  options: { internal?: boolean } = {},
): Promise<void> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(64)));
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const challenge = await pkceChallenge(verifier);
  const params = new URLSearchParams({
    client_id: clientId(),
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: redirectUri(),
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  if (!options.internal) {
    params.set('identity_provider', 'EntraID');
  }
  window.location.assign(`${domain()}/oauth2/authorize?${params.toString()}`);
}

/**
 * End the hosted-UI session so the next sign-in is a fresh, logged decision.
 * Without this, Cognito's session cookie survives app logout and the next
 * "sign in" click silently resumes the previous user. `logout_uri` must
 * exactly match a URL registered on the app client.
 */
export function endSsoSession(): void {
  const params = new URLSearchParams({
    client_id: clientId(),
    logout_uri: `${window.location.origin}/`,
  });
  window.location.assign(`${domain()}/logout?${params.toString()}`);
}

/** Exchange the callback ?code= for tokens; returns the id_token. */
export async function completeSsoLogin(code: string): Promise<string> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  if (!verifier) {
    throw new Error('Missing PKCE verifier — restart the sign-in.');
  }
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId(),
    code,
    redirect_uri: redirectUri(),
    code_verifier: verifier,
  });
  const res = await fetch(`${domain()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!res.ok) {
    throw new Error(`Token exchange failed (${res.status})`);
  }
  const data = (await res.json()) as { id_token?: string };
  if (!data.id_token) {
    throw new Error('Token exchange returned no id_token');
  }
  return data.id_token;
}
